// web-admin/src/pages/attendance/AttendanceImport.js
// Fingerprint Device Attendance Import Page
// Global HR standard: Upload → Detect & Preview → Validate → Confirm → Commit

import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Button, Stepper, Step, StepLabel,
  Chip, Alert, AlertTitle, CircularProgress, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, Card, CardContent, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import MergeIcon from "@mui/icons-material/MergeType";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

import AppHeader from "../../components/AppHeader";

ModuleRegistry.registerModules([AllCommunityModule]);

const API_URL = process.env.REACT_APP_ATTENDANCE_API_URL || "http://localhost:3000/api/attendance";

const getTenantHeader = () => ({ "X-Tenant": localStorage.getItem("tenant") || "" });

// ── Status chip renderer ──────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const config = {
    MATCHED:   { label: "Matched",    color: "success", icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    NOT_FOUND: { label: "Not Found",  color: "error",   icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
    DUPLICATE: { label: "Merge",      color: "info",    icon: <MergeIcon sx={{ fontSize: 14 }} /> },
  }[status] || { label: status, color: "default", icon: null };

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      color={config.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

// ── Steps definition ──────────────────────────────────────────────────────
const STEPS = ["Upload File", "Preview Data", "Confirm Import"];

// ─────────────────────────────────────────────────────────────────────────────
export default function AttendanceImport({ setIsLoggedIn }) {
  const [activeStep, setActiveStep] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Step 1 state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [dateFormatPrompt, setDateFormatPrompt] = useState(null);

  // Step 2 state (preview)
  const [previewData, setPreviewData] = useState(null); // { rows, stats, detected_format }
  const [rowData, setRowData] = useState([]);

  // Step 3 state (commit)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null); // { imported, merged, skipped, errors }

  const gridRef = useRef(null);

  // ── Column definitions for the preview grid ───────────────────────────
  const columnDefs = [
    {
      headerName: "#",
      field: "preview_id",
      width: 60,
      pinned: "left",
      cellStyle: { color: "#666", fontSize: "0.75rem" },
    },
    {
      headerName: "Status",
      field: "match_status",
      width: 120,
      pinned: "left",
      cellRenderer: (params) => <StatusChip status={params.value} />,
    },
    {
      headerName: "Emp No.",
      field: "employee_number",
      width: 110,
      cellStyle: { fontWeight: 600 },
    },
    {
      headerName: "Name (Excel)",
      field: "excel_name",
      width: 160,
    },
    {
      headerName: "Name (System)",
      field: "staff_name",
      width: 160,
      cellRenderer: (params) =>
        params.value ? (
          <Typography variant="body2" sx={{ color: "#1b5e20", fontWeight: 500 }}>
            {params.value}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: "#b71c1c", fontStyle: "italic" }}>
            Not found
          </Typography>
        ),
    },
    {
      headerName: "Date",
      field: "date",
      width: 120,
    },
    {
      headerName: "Clock In",
      field: "clock_in_raw",
      width: 100,
      cellStyle: { color: "#1565c0", fontWeight: 600 },
    },
    {
      headerName: "Clock Out",
      field: "clock_out_raw",
      width: 100,
      cellStyle: { color: "#4a148c", fontWeight: 600 },
    },
    {
      headerName: "Department",
      field: "department",
      width: 140,
    },
    {
      headerName: "Existing In",
      field: "existing_clock_in",
      width: 120,
      cellRenderer: (params) => {
        if (!params.value) return "-";
        const d = new Date(params.value);
        return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
      },
      cellStyle: { color: "#e65100", fontSize: "0.75rem" },
    },
    {
      headerName: "Existing Out",
      field: "existing_clock_out",
      width: 120,
      cellRenderer: (params) => {
        if (!params.value) return "-";
        const d = new Date(params.value);
        return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
      },
      cellStyle: { color: "#e65100", fontSize: "0.75rem" },
    },
  ];

  const defaultColDef = { sortable: true, filter: true, resizable: true };

  // ── Row styling based on match status ─────────────────────────────────
  const getRowStyle = useCallback((params) => {
    const status = params.data?.match_status;
    if (status === "NOT_FOUND") return { background: "#fff5f5", borderLeft: "3px solid #ef5350" };
    if (status === "DUPLICATE") return { background: "#e3f2fd", borderLeft: "3px solid #42a5f5" };
    return { background: "#f1f8e9", borderLeft: "3px solid #66bb6a" };
  }, []);

  // ── File selection & validation ───────────────────────────────────────
  const handleFileSelect = (f) => {
    setUploadError("");
    setImportResult(null);
    setPreviewData(null);
    setRowData([]);
    setDateFormatPrompt(null);
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "xlsb", "txt", "csv"].includes(ext)) {
      setUploadError("Only .xlsx, .xls, .xlsb, .txt, and .csv files are supported.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      setUploadError("File is too large. Maximum size is 10MB.");
      return;
    }
    setFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  // ── Upload → Preview (Step 1 → 2) ────────────────────────────────────
  const handleUploadPreview = async (dateHint = null) => {
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (dateHint) formData.append("date_hint", dateHint);

      const res = await axios.post(`${API_URL}/admin/import/preview`, formData, {
        withCredentials: true,
        headers: { ...getTenantHeader(), "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        setPreviewData(res.data);
        setRowData(res.data.rows || []);
        setActiveStep(1);
      } else {
        setUploadError(res.data.message || "Failed to parse file.");
      }
    } catch (err) {
      const responseData = err.response?.data;
      if (responseData?.requires_date_format) {
        setDateFormatPrompt(responseData.date_format_info || {});
        return;
      }
      const msg = responseData?.message || err.message || "Upload failed.";
      setUploadError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDateFormatChoice = (dateHint) => {
    setDateFormatPrompt(null);
    handleUploadPreview(dateHint);
  };

  // ── Commit Import (Step 2 → 3) ────────────────────────────────────────
  const handleCommit = async () => {
    setConfirmOpen(false);
    setImporting(true);
    setImportResult(null);
    try {
      // Only send rows that are importable (not NOT_FOUND)
      const importableRows = rowData.filter((r) => r.match_status !== "NOT_FOUND");

      const res = await axios.post(
        `${API_URL}/admin/import/commit`,
        { rows: importableRows, on_duplicate: "merge" },
        { withCredentials: true, headers: getTenantHeader() }
      );

      if (res.data.success) {
        setImportResult(res.data.results);
        setActiveStep(2);
      } else {
        setUploadError(res.data.message || "Import failed.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Import failed.";
      setUploadError(msg);
    } finally {
      setImporting(false);
    }
  };

  // ── Export import log as CSV ──────────────────────────────────────────
  const handleExportLog = () => {
    if (!importResult) return;
    const lines = [
      "Row,Employee No.,Name,Date,Status,Reason",
      ...rowData.map((r) => {
        const wasSkipped = importResult.errors?.some((e) => e.row === r.preview_id);
        const wasMerged = r.match_status === "DUPLICATE";
        const status = wasSkipped ? "Skipped" : wasMerged ? "Merged" : "Imported";
        const reason = importResult.errors?.find((e) => e.row === r.preview_id)?.reason || "";
        return `${r.preview_id},"${r.employee_number || ""}","${r.excel_name || ""}","${r.date || ""}","${status}","${reason}"`;
      }),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_import_log_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Reset everything ──────────────────────────────────────────────────
  const handleReset = () => {
    setActiveStep(0);
    setFile(null);
    setPreviewData(null);
    setRowData([]);
    setImportResult(null);
    setUploadError("");
    setDateFormatPrompt(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f4f6f8" }}>
      <AppHeader setIsLoggedIn={setIsLoggedIn} />

      <Box sx={{ p: 3, maxWidth: 1400, mx: "auto", width: "100%" }}>
        {/* Page Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
          <FingerprintIcon sx={{ fontSize: 32, color: "#1565c0" }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="#1a237e">
              Fingerprint Device Import
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Import attendance records from fingerprint device Excel exports
            </Typography>
          </Box>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Stepper activeStep={activeStep}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* ── STEP 0: Upload ─────────────────────────────────────────── */}
        {activeStep === 0 && (
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={1}>
              Upload Fingerprint Export File
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Accepts fingerprint export files in <strong>Format A</strong> (header on row 1),{" "}
              <strong>Format B</strong> (header on row 3), <strong>SEANET</strong>, or <strong>OnTime</strong>. Supported file types:{" "}
              <strong>.xlsx</strong>, <strong>.xls</strong>, <strong>.xlsb</strong>, <strong>.txt</strong>, and <strong>.csv</strong>.
            </Typography>

            <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 3 }}>
              <AlertTitle>Auto-Detection + Date Format Verification</AlertTitle>
              The system will auto-detect the file layout (header row, column positions) and sample
              up to 30 date values to verify the date format. You will see a confirmation banner
              in the next step showing the detected date format and sample dates.
            </Alert>

            {/* Drag-drop zone */}
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? "#1565c0" : file ? "#2e7d32" : "#bdbdbd"}`,
                borderRadius: 3,
                p: 6,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragOver ? "#e3f2fd" : file ? "#f1f8e9" : "#fafafa",
                transition: "all 0.2s",
                "&:hover": { borderColor: "#1565c0", bgcolor: "#e3f2fd" },
              }}
            >
                <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.xlsb,.txt,.csv"
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
              />
              <CloudUploadIcon sx={{ fontSize: 56, color: file ? "#2e7d32" : "#9e9e9e", mb: 1 }} />
              {file ? (
                <>
                  <Typography variant="h6" color="#2e7d32" fontWeight={600}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB — Click or drag to replace
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="text.secondary">
                    Drop your Excel file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse
                  </Typography>
                </>
              )}
            </Box>

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            {dateFormatPrompt && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>Date format confirmation required</AlertTitle>
                Dates like <strong>{dateFormatPrompt.samples?.[0] || "7/4/2026"}</strong> can mean two different calendar dates. Please choose the file's date order to continue.
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                onClick={() => handleUploadPreview()}
                disabled={!file || uploading}
                sx={{ px: 4, py: 1.5, bgcolor: "#1565c0", "&:hover": { bgcolor: "#0d47a1" } }}
              >
                {uploading ? "Parsing File..." : "Parse & Preview"}
              </Button>
            </Box>
          </Paper>
        )}

        {/* ── STEP 1: Preview Grid ────────────────────────────────────── */}
        {activeStep === 1 && previewData && (
          <Box>
            {/* Stats cards */}
            <Grid container spacing={2} mb={2}>
              {[
                {
                  label: "Total Rows",
                  value: previewData.stats.total,
                  color: "#1565c0",
                  bg: "#e3f2fd",
                },
                {
                  label: "✅ Matched",
                  value: previewData.stats.matched,
                  color: "#2e7d32",
                  bg: "#f1f8e9",
                },
                {
                  label: "🔵 Merge (Duplicate)",
                  value: previewData.stats.duplicate,
                  color: "#0277bd",
                  bg: "#e1f5fe",
                },
                {
                  label: "🔴 Not Found",
                  value: previewData.stats.not_found,
                  color: "#c62828",
                  bg: "#ffebee",
                },
              ].map((card) => (
                <Grid item xs={6} sm={3} key={card.label}>
                  <Card sx={{ bgcolor: card.bg, border: `1px solid ${card.color}22` }}>
                    <CardContent sx={{ pb: "12px !important", pt: 1.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        {card.label}
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Format & date format info */}
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>Format {previewData.detected_format} detected</strong> —{" "}
              {previewData.stats.matched + previewData.stats.duplicate} records will be imported
              {previewData.stats.duplicate > 0 &&
                ` (${previewData.stats.duplicate} will be merged with existing mobile clock-in records)`}
              {previewData.stats.not_found > 0 &&
                `. ${previewData.stats.not_found} rows will be skipped (employee not found in system).`}
            </Alert>

            {/* Date format detection info */}
            {previewData.date_format_info && (
              <Alert
                severity={previewData.date_format_info.isAmbiguous ? "warning" : "info"}
                sx={{ mb: 2 }}
              >
                <AlertTitle>
                  📅 Date Format{previewData.date_format_info.isAmbiguous ? " — Please Verify" : " Detected"}
                </AlertTitle>
                Detected format:{" "}
                <strong>{previewData.date_format_info.detectedFormat}</strong>
                {previewData.date_format_info.hint === "MDY" && (
                  <span> (month-first — e.g. Jan 5 = <code>1/5/2026</code>)</span>
                )}
                {previewData.date_format_info.hint === "DMY" && previewData.date_format_info.detectedFormat !== "DD-Mon-YYYY" && (
                  <span> (day-first — e.g. Jan 5 = <code>5/1/2026</code>)</span>
                )}
                {previewData.date_format_info.samples?.length > 0 && (
                  <Box component="span" sx={{ display: "block", mt: 0.5, fontSize: "0.78rem", opacity: 0.8 }}>
                    Sample values: {previewData.date_format_info.samples.join(" · ")}
                  </Box>
                )}
                {previewData.date_format_info.confirmedByUser && (
                  <Box component="span" sx={{ display: "block", mt: 0.5, fontWeight: 600 }}>
                    Confirmed by user before preview. Please verify the parsed dates in the grid before importing.
                  </Box>
                )}
              </Alert>
            )}

            {/* Legend */}
            <Box sx={{ display: "flex", gap: 2, mb: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: "#66bb6a", borderRadius: 1 }} />
                <Typography variant="caption">Matched — will import</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: "#42a5f5", borderRadius: 1 }} />
                <Typography variant="caption">Merge — earliest clock-in, latest clock-out wins</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: "#ef5350", borderRadius: 1 }} />
                <Typography variant="caption">Not Found — will be skipped</Typography>
              </Box>
            </Box>

            {/* AG Grid Preview */}
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box className="ag-theme-quartz" sx={{ height: 500, width: "100%" }}>
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  getRowStyle={getRowStyle}
                  rowHeight={40}
                  suppressCellFocus={true}
                  animateRows={true}
                />
              </Box>
            </Paper>

            {/* Not-found warning */}
            {previewData.stats.not_found > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <AlertTitle>
                  {previewData.stats.not_found} employee(s) not found in PeopleHub
                </AlertTitle>
                These rows (shown in red) will be skipped. Please ensure staff have their{" "}
                <strong>Employee Number</strong> (from PeopleHub) entered in the device's{" "}
                <strong>No.</strong> column (for files with a Staff Code column) or{" "}
                <strong>AC-No.</strong> column (for files that use the device ID directly as the PH employee number).
              </Alert>
            )}

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {uploadError}
              </Alert>
            )}

            {/* Navigation */}
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleReset}
              >
                Upload Different File
              </Button>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setConfirmOpen(true)}
                disabled={previewData.stats.matched + previewData.stats.duplicate === 0}
                sx={{ px: 4, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
              >
                Import {previewData.stats.matched + previewData.stats.duplicate} Records
              </Button>
            </Box>
          </Box>
        )}

        <Dialog
          open={Boolean(dateFormatPrompt)}
          onClose={() => setDateFormatPrompt(null)}
          PaperProps={{ sx: { borderRadius: 3, maxWidth: 520 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Confirm Date Format</DialogTitle>
          <DialogContent dividers>
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
              The uploaded file contains ambiguous date values. Choose the format used by the fingerprint device export before previewing.
            </Alert>
            {dateFormatPrompt?.samples?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Sample values
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {dateFormatPrompt.samples.map((sample, idx) => (
                    <Chip key={`${sample}-${idx}`} label={sample} size="small" />
                  ))}
                </Stack>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary">
              Example for <strong>7/4/2026</strong>: MM/DD/YYYY means July 4, 2026. DD/MM/YYYY means April 7, 2026.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setDateFormatPrompt(null)}>Cancel</Button>
            <Button variant="outlined" onClick={() => handleDateFormatChoice("DMY")}>
              DD/MM/YYYY
            </Button>
            <Button variant="contained" onClick={() => handleDateFormatChoice("MDY")}>
              MM/DD/YYYY
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── STEP 2: Result ──────────────────────────────────────────── */}
        {activeStep === 2 && importResult && (
          <Paper sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: "#2e7d32", mb: 1 }} />
              <Typography variant="h5" fontWeight={700} color="#2e7d32">
                Import Complete!
              </Typography>
              <Typography variant="body1" color="text.secondary" mt={1}>
                Fingerprint attendance records have been saved. Late detection, OT evaluation,
                and salary deductions have been recalculated automatically.
              </Typography>
            </Box>

            {/* Result stats */}
            <Grid container spacing={2} mb={4} justifyContent="center">
              {[
                { label: "New Records", value: importResult.imported, color: "#2e7d32", bg: "#f1f8e9" },
                { label: "Merged Records", value: importResult.merged, color: "#0277bd", bg: "#e1f5fe" },
                { label: "Skipped", value: importResult.skipped, color: "#e65100", bg: "#fff3e0" },
              ].map((card) => (
                <Grid item xs={4} sm={3} key={card.label}>
                  <Card sx={{ bgcolor: card.bg, textAlign: "center" }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Errors table */}
            {importResult.errors?.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Skipped Rows ({importResult.errors.length})</AlertTitle>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row #</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importResult.errors.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell>{e.row}</TableCell>
                          <TableCell>{e.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportLog}
              >
                Download Import Log (CSV)
              </Button>
              <Button
                variant="contained"
                onClick={handleReset}
                sx={{ bgcolor: "#1565c0" }}
              >
                Import Another File
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Importing progress overlay */}
        {importing && (
          <Box
            sx={{
              position: "fixed", inset: 0, bgcolor: "rgba(0,0,0,0.4)",
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", zIndex: 9999,
            }}
          >
            <Paper sx={{ p: 4, borderRadius: 2, minWidth: 320, textAlign: "center" }}>
              <FingerprintIcon sx={{ fontSize: 48, color: "#1565c0", mb: 2 }} />
              <Typography variant="h6" fontWeight={600} mb={1}>
                Importing Attendance Records...
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Running OT evaluation, late detection &amp; deduction recalculation.
                Please wait.
              </Typography>
              <LinearProgress sx={{ borderRadius: 1 }} />
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Confirm Dialog ─────────────────────────────────────────────── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm Import</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              You are about to import <strong>{previewData?.stats?.matched + previewData?.stats?.duplicate || 0}</strong> attendance records.
            </Alert>

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                What will happen:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2">
                    <strong>{previewData?.stats?.matched || 0}</strong> new records will be inserted with{" "}
                    <code>clock_in_method = 'FINGERPRINT'</code>
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <MergeIcon sx={{ color: "#0277bd", fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2">
                    <strong>{previewData?.stats?.duplicate || 0}</strong> records will be merged with existing
                    mobile data (earliest clock-in / latest clock-out wins)
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2">
                    Late detection, early leave, allowances, and salary deductions will be
                    automatically recalculated
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 18, mt: 0.3 }} />
                  <Typography variant="body2">
                    OT Engine will run for eligible employees
                  </Typography>
                </Box>
                {previewData?.stats?.not_found > 0 && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <WarningAmberIcon sx={{ color: "#e65100", fontSize: 18, mt: 0.3 }} />
                    <Typography variant="body2" color="error.main">
                      <strong>{previewData?.stats?.not_found}</strong> rows will be skipped (employee not found)
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleCommit}
            variant="contained"
            sx={{ bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" }, px: 3 }}
          >
            ✓ Confirm Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
